
import { defineComponent, PropType, reactive, ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import BaseRoundRectButton from "@/components/atoms/BaseRoundRectButton.vue";
import SignInContainer from "@/components/container/SignInContainer";
import SignInModalMaterial from "@/components/common_presentational/SignInModal.vue";
import Dialog from "@/components/atoms/Dialog.vue";
import AppDownloadModal from "@/components/common_presentational/AppDownloadModal.vue";
import { LiveStreamMethodType } from "@/entities/fan_meeting";
import Loading from "@/components/atoms/Loading.vue";
import Imgix, { ImageSize } from "@/components/atoms/Imgix.vue";
import Talent from "@/entities/talent";
import Fan from "@/entities/fan";
import { itemCodes } from "@/util/item_code"
import { FanMeetingAndReserved } from "@/entities/fan_meeting_and_reserved";
import { CommunicationState } from "@/type";

const SignInModal = SignInContainer(SignInModalMaterial);

export default defineComponent({
  name: "TalentInfo",
  components: {
    BaseRoundRectButton,
    AppDownloadModal,
    Imgix,
    Dialog,
    SignInModal,
    Loading
  },
  props: {
    talent: {
      type: Object as PropType<Talent>,
      required: true,
    },
    fan: {
      type: Object as PropType<Fan | undefined>,
      required: true,
    },
    nowFanMeeting: {
      type: Object as PropType<FanMeetingAndReserved | undefined>,
      required: true,
    },
    schedule: {
      type: Array as PropType<FanMeetingAndReserved[]>,
      required: true,
    },
    balance: {
      type: Number,
      required: true,
    },
    communicationState: {
      type: Object as () => CommunicationState,
      default: 1,
      required: true,
    },
  },
  emits: {
    createReservation(fanMeetingId: number) {
      if (fanMeetingId != null) return true;
    },
  },
  setup(props, { emit }) {
    const state = reactive({
      isActive: true,
      isOpenModal: false,
      isOpenSignInModal: false,
      isOpenReservationModal: false,
      isOpenAppOpenGuideModal: false,
    });

    const router = useRouter()

    function pushShop() {
      router.push("/shop")
    }

    function displayTime(time: number) {
      const min = Math.floor(time / 60);
      const sec = time % 60;

      let displayTime = ""

      if (1 <= min) {
        displayTime = min + "分";
      }

      if (1 <= sec) {
        displayTime += sec + "秒"
      }


      return displayTime
    }

    const isLoading = ref<boolean>(false);

    watch(
      () => props.communicationState,
      () => {
        if (props.communicationState === CommunicationState.doing) {
          isLoading.value = true;
        } else {
          isLoading.value = false;
        }
      }
    );

    const selectedFanMeeting = ref<FanMeetingAndReserved>()

    const coinNum = computed(() => {
      return itemCodes.find((e) => e[0] === selectedFanMeeting.value?.fanMeeting.itemCode)?.[1]
    })

    function reserveText(fanMeetingReserved: FanMeetingAndReserved) {
      if (fanMeetingReserved.isReserved) {
        return "予約済み"
      }
      if (props.balance < (fanMeetingReserved.fanMeeting.chekiPrice + (coinNum.value as number))) {
        return "残高が不足しています"
      }
      return "予約する"
    }

    function createReservation() {
      emit("createReservation", selectedFanMeeting.value?.fanMeeting.id ?? 0);
    }

    const talentID = useRoute().params.talent_id;

    const imageSize: ImageSize = {
      sm: {
        width: window.innerWidth,
        height: 500,
      },
      md: {
        width: 300,
        height: 400,
      },
      lg: {
        width: 300,
        height: 400,
      },
    };

    const iconImageSize: ImageSize = {
      sm: {
        width: 25,
        height: 25,
      },
      md: {
        width: 25,
        height: 25,
      },
      lg: {
        width: 25,
        height: 25,
      },
    };

    // メソッド
    function active_intro() {
      state.isActive = false;
    }
    function active_schedule() {
      state.isActive = true;
    }
    function openModal() {
      state.isOpenModal = true;
    }
    function closeModal() {
      state.isOpenModal = false;
    }

    const encodedTalentID = encodeURIComponent(talentID as string);
    const talentUrl = `${window.location.origin}/talent/${encodedTalentID}`;
    const appLink = `${window.location.origin}/share?talentID=${encodedTalentID}`;
    const iosScheme = window.location.hostname === "stg.onlylive.jp" ? "onlylive-stg" : "onlylive";
    const iosDeepLink = `${iosScheme}://share?talentID=${encodedTalentID}`;
    const androidPackage = window.location.hostname === "stg.onlylive.jp"
      ? "com.ILEO.onlylive.development"
      : "com.ILEO.onlylive";
    const androidDeepLink = `intent://${window.location.hostname}/share?talentID=${encodedTalentID}#Intent;scheme=${window.location.protocol.replace(":", "")};package=${androidPackage};S.browser_fallback_url=${encodeURIComponent(talentUrl)};end`;
    const copyLinkText = ref("リンクをコピー");

    function closeAppOpenGuide() {
      state.isOpenAppOpenGuideModal = false;
      copyLinkText.value = "リンクをコピー";
    }

    function copyAppLinkWithTextArea() {
      const textArea = document.createElement("textarea");
      textArea.value = appLink;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }

    async function copyAppLink() {
      try {
        if (navigator.clipboard !== undefined) {
          await navigator.clipboard.writeText(appLink);
        } else {
          copyAppLinkWithTextArea();
        }
        copyLinkText.value = "コピーしました";
      } catch {
        try {
          copyAppLinkWithTextArea();
          copyLinkText.value = "コピーしました";
        } catch {
          copyLinkText.value = "コピーできませんでした";
        }
      }
    }

    function openAppDeepLink() {
      let cleanupFallback: () => void = () => undefined;
      const cancelFallback = () => {
        if (document.hidden) cleanupFallback();
      };
      cleanupFallback = () => {
        document.removeEventListener("visibilitychange", cancelFallback);
        window.removeEventListener("pagehide", cleanupFallback);
        window.removeEventListener("blur", cleanupFallback);
      };
      document.addEventListener("visibilitychange", cancelFallback);
      window.addEventListener("pagehide", cleanupFallback);
      window.addEventListener("blur", cleanupFallback);

      const userAgent = window.navigator.userAgent.toLowerCase();
      if (userAgent.indexOf('iphone') !== -1 || userAgent.indexOf('ipad') !== -1) {
        window.location.href = iosDeepLink;
      } else if (userAgent.indexOf('android') !== -1) {
        window.location.href = androidDeepLink;
      } else {
        window.location.href = appLink;
      }
    }

    function openIOSdeeplink() {
      state.isOpenAppOpenGuideModal = true;
    }

    function openAndroiddeeplink() {
      state.isOpenAppOpenGuideModal = true;
    }

    function openReservationDialog(fanMeeting: FanMeetingAndReserved) {
      selectedFanMeeting.value = fanMeeting;
      state.isOpenAppOpenGuideModal = true;
    }

    return {
      state,
      active_intro,
      active_schedule,
      openModal,
      closeModal,
      imageSize,
      iosDeepLink,
      androidDeepLink,
      appLink,
      copyLinkText,
      createReservation,
      openReservationDialog,
      iconImageSize,
      selectedFanMeeting,
      LiveStreamMethodType,
      itemCodes,
      coinNum,
      pushShop,
      displayTime,
      isLoading,
      reserveText,
      openIOSdeeplink,
      openAndroiddeeplink,
      openAppDeepLink,
      closeAppOpenGuide,
      copyAppLink
    };
  },
});
